import tokens from '@kluein/design-tokens';
import { Box, Tip, Anchor } from '@kluein/klue-ui';
import { StackSimple } from '@phosphor-icons/react';
import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { useBattlecardCardsHighlightingContext } from 'hooks/use-battlecard-cards-highlighting/useBattlecardCardsHighlighting';

type KlueCardBattlecardIndicatorProps = {
  cardId: number;
  profileId?: number;
};

const KlueCardBattlecardIndicator = ({
  cardId,
  profileId,
}: KlueCardBattlecardIndicatorProps) => {
  const navigate = useNavigate();
  const [isHighlighted, setIsHighlighted] = useState(false);
  const { addCardObserver, removeCardObserver, onBattlecard, getBattlecards } =
    useBattlecardCardsHighlightingContext();

  const isOnBattlecard = onBattlecard(cardId);
  const setHighlighted = useCallback(
    (highlighted: boolean) => setIsHighlighted(highlighted),
    [],
  );

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (node) {
        addCardObserver(cardId, node, setHighlighted);
      } else {
        removeCardObserver(cardId);
      }
    },
    [addCardObserver, cardId, removeCardObserver, setHighlighted],
  );

  const getTipContent = useMemo(() => {
    const battlecards = getBattlecards(cardId);
    if (!battlecards) {
      return null;
    }
    return (
      <Box pad="small" align="start" gap={tokens.spacing.xsmall}>
        {battlecards.map((bc) => (
          <Anchor
            onClick={
              profileId
                ? () =>
                    navigate(`/profile/${profileId}/battlecard/view/${bc.id}`)
                : undefined
            }
            key={bc.id}
            variant="longform-neutral-inverted"
            label={bc.title}
          />
        ))}
      </Box>
    );
  }, [cardId, getBattlecards, navigate, profileId]);

  if (!isOnBattlecard) {
    return null;
  }

  return (
    <Box ref={setRef} flex={false}>
      <Tip color="dark" variant="xsmall" content={getTipContent}>
        <Box
          background={
            isHighlighted
              ? tokens.color.primitives.green[100]
              : tokens.color.primitives.white
          }
          pad={tokens.spacing.small}
          round={tokens.borderRadius.full}
          align="center"
          justify="center"
        >
          <StackSimple size={tokens.size.xxsmall} />
        </Box>
      </Tip>
    </Box>
  );
};

export default KlueCardBattlecardIndicator;
